import React, { useState } from 'react';
import GridIcon from '../icons/GridIcon';
import ChevronDownIcon from '../icons/ChevronDownIcon';

const DashboardMenu = ({ 
  items = [], 
  activeItemId = null,
  onItemClick = () => {},
  className = '',
  ...props 
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set();
    if (!expandedItems.has(itemId)) {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleItemClick = (item, level = 0) => {
    if (item.children && item.children.length > 0) {
      toggleExpanded(item.id);
      // Don't set root items with children as active
      return;
    }
    // Only collapse expanded items when activating a root level item (level 0)
    if (level === 0) {
      setExpandedItems(new Set());
    }
    onItemClick(item);
  };

  const renderMenuItem = (item, level = 0) => {
    const isActive = activeItemId === item.id;
    const isExpanded = expandedItems.has(item.id);
    const hasChildren = item.children && item.children.length > 0;
    const isSubItem = level > 0;

    const baseClasses = `
      flex items-center justify-between
      w-full h-[38px] px-3 py-0
      rounded-xl
      transition-colors transition-shadow duration-200 ease-in-out
      cursor-pointer
      group
    `;

    // Different styling for root items vs sub-items
    const stateClasses = isSubItem ? (
      isActive ? `
        bg-[#FFFFFF] dark:bg-[#121212]
      ` : `
        bg-[#FFFFFF] dark:bg-[#121212]
      `
    ) : (
      isActive || isExpanded ? `
        bg-[#F1F1F1] dark:bg-[#2A2A2A]
        border border-[#ECECEC] dark:border-[#202020]
      ` : `
        hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A]
        focus:bg-[#F1F1F1] dark:focus:bg-[#2A2A2A]
        ${isExpanded ? 'bg-[#F1F1F1] dark:bg-[#2A2A2A]' : ''}
      `
    );

    const textClasses = isSubItem ? (
      isActive ? `
        text-[#121212] dark:text-[#D0D0D0]
        font-semibold
      ` : `
        text-[#7B7B7B] dark:text-[#848484]
        hover:text-[#121212] dark:hover:text-[#D0D0D0]
        font-semibold
      `
    ) : (
      isActive ? `
        text-[#121212] dark:text-[#D0D0D0]
        font-semibold
      ` : `
        text-[#121212] dark:text-[#D0D0D0]
        font-semibold
      `
    );

    const iconClasses = isActive || isExpanded ? `
      text-[#121212] dark:text-[#D0D0D0]
    ` : `
      text-[#7B7B7B] dark:text-[#848484]
    `;

    const chevronClasses = `
      text-[#7B7B7B] dark:text-[#848484]
      transition-transform duration-200 ease-in-out
      ${isExpanded ? 'rotate-180' : ''}
    `;

    return (
      <div key={item.id} className="w-full">
        <button
          className={`${baseClasses} ${stateClasses}`}
          onClick={() => handleItemClick(item, level)}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-current={isActive ? 'page' : undefined}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Icon Container - only for root items */}
            {!isSubItem ? (
              <div className={`
                w-8 h-8 rounded-lg
                flex items-center justify-center
                ${isActive || isExpanded ? 'bg-[#FCFCFC] dark:bg-[#1A1A1A] shadow-[0_0_4px_rgba(18,18,18,0.08)]' : ''}
                flex-shrink-0
              `}>
                {item.icon ? (
                  <item.icon 
                    size={20} 
                    className={iconClasses}
                  />
                ) : (
                  <GridIcon 
                    size={20} 
                    className={iconClasses}
                  />
                )}
              </div>
            ) : (
              /* No spacer for sub-items - they align with root text */
              null
            )}

            {/* Text */}
            <span className={`
              ${textClasses}
              text-xs font-semibold leading-4 tracking-[-0.1px]
              truncate
            `}>
              {item.label}
            </span>
          </div>

          {/* Chevron for items with children */}
          {hasChildren && (
            <div className="flex-shrink-0 ml-2">
              <ChevronDownIcon 
                size={16} 
                className={chevronClasses}
              />
            </div>
          )}
        </button>

        {/* Submenu */}
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.children.map((child) => (
              <div key={child.id} className="ml-11">
                {renderMenuItem(child, level + 1)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav 
      className={`flex flex-col space-y-1 ${className}`}
      role="navigation"
      aria-label="Dashboard menu"
      {...props}
    >
      {items.map((item) => renderMenuItem(item))}
    </nav>
  );
};

export default DashboardMenu;
